﻿import React, { useState, useEffect } from "react";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Container,
} from "@mui/material";
import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import {
  selectEditConnectionModifiedConnectionPairs,
  selectEditConnectionConnectionPairWorkflow,
  selectConnectionModel,
  fetchOperations,
  fetchConnectionProfiles,
  fetchEndpoints,
  fetchConnectionIds,
  fetchConnectionSummaries,
  fetchConnectionAllPairs,
  setSelectedConnection,
  selectEditConnectionOpen,
  selectEditSchemaOpen,
  setEditConnectionId,
  selectAllConnectionSummaries,
  selectSelectedTemplateInfo,
  loadSchemaCollection,
  selectSelectedConnection,
  setSchemaCollectionAdded,
  setIsLoadCollectionClicked,
  selectEditSchemaTabValue,
  selectEditConnectionId,
  setTriggerRefreshForConnectionSummary,
  selectMapTabSelected,
  setMapTabSelected,
  selectConnectionSummaryRecords,
  selectEditConnectionAllConnections,
  selectCatalogSharingSetupDetails,
  selectConnectionPairObject,
} from "@/features/connections/integrationSlice";
import { ShepTab, ShepTabs } from "@/components/GenericComponents/ShepTab";
import ShepButton from "@/components/GenericComponents/ShepButton";
import Connection from "@/components/Integrations/ManageConnection/Connection";
import Schema from "@/components/Integrations/ManageConnection/Schema";
import ConnectionPair from "@/components/Integrations/ManageConnection/ConnectionPair";
import Workflows from "@/components/Integrations/ManageConnection/Workflows";
import Endpoint from "@/components/Integrations/ManageConnection/Endpoints";
import { useDispatch, useSelector } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  setIsMappingClicked,
  selectIsMappingClicked,
  selectSelectedSchema,
} from "@/features/mapSchema/mapSchemaSlice";
import {
  handleEditConnection,
  EndpointType,
  validateForm,
  SchemaType,
  getEndpointSchema,
} from "@/components/Integrations/ManageConnection/ManageConnectionHelper";
import {
  ManageConnectionPopupProvider,
  FooterBarHost,
  FooterButton,
  SubtabBarHost,
  useManageConnectionFooter,
} from "@/components/Integrations/ManageConnection/ManageConnectionPopupContext";

const AddConnectionPopupInner = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const selectedConnection = useSelector(selectSelectedConnection);
  const connectionSummaries = useSelector(selectAllConnectionSummaries);

  //this info contains the updated row info.
  const rows = useSelector(selectConnectionSummaryRecords);
  const id = useSelector(selectEditConnectionId);

  const [showErrors, setShowErrors] = useState(false);
  const [tabErrors, setTabErrors] = useState({
    connectionDetails: false,
    endpoints: false,
    schema: false,
    connectionPair: false,
    workflows: false,
  }); // State to track validation errors for each tab

  //Variables for multiple tabs
  const connectionId = useSelector(selectEditConnectionId);

  //States for Connection Details tab
  const [connectionDetails, setConnectionDetails] = useState({
    connectionName: "",
    displayName: "",
    connectionType: "",
    shortForm: "",
    isDisabled: false,
  });

  const [connectionTabValue, setConnectionTabValue] = useState(0);

  //Variables for Endpoints Screen
  const [endpointId, setEndpointId] = useState(null);

  //Variables for Connection Pair
  const [modifiedConnectionPairs, setModifiedConnectionPairs] = useState([]);

  useEffect(() => {
    dispatch(fetchEndpoints());
    dispatch(fetchConnectionIds());
  }, [dispatch]);
  //Workflow Tab

  const isEditConnectionOpen = useSelector(selectEditConnectionOpen);
  const isEditSchemaOpen = useSelector(selectEditSchemaOpen);
  const connectionPairModel = useSelector(
    selectEditConnectionModifiedConnectionPairs
  );
  const workflowModel = useSelector(selectEditConnectionConnectionPairWorkflow);
  const catalogSharingSetupDetails = useSelector(
    selectCatalogSharingSetupDetails
  );
  const connectionPairObject = useSelector(selectConnectionPairObject);

  const connectionModel = useSelector(selectConnectionModel);
  const openDialog = isEditConnectionOpen || isEditSchemaOpen;

  const [tabValue, setTabValue] = useState(0);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const [clearFormKey, setClearFormKey] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    dispatch(setMapTabSelected(false));
  };

  const saveNewConnection = (connectionDetails, endpointId, requestId) => {
    const jsonProperties = JSON.stringify({
      ShortFormId: connectionDetails.shortForm,
      DisplayName: connectionDetails.displayName,
      ConnectionType: connectionDetails.connectionType,
    });

    return axios
      .post(
        "/connections",
        JSON.stringify({
          connectionId: -1,
          connectionName: connectionDetails.connectionName,
          JSONProperties: jsonProperties,
          endpointId: endpointId ?? 0,
          schemaCollectionId: null,
          isDisabled: connectionDetails.isDisabled,
        })
      )
      .catch((error) => {
        console.error("Error saving draft", error);
      });
  };

  const handleClose = () => {
    setConfirmationOpen(false);
    dispatch(
      setSchemaCollectionAdded({
        schemaCollectionAdded: [],
      })
    );
    onClose();
  };

  const saveButtonClicked = async () => {
    if (
      !validateForm(
        tabErrors,
        setTabValue,
        dispatch,
        setShowErrors,
        setConnectionTabValue
      )
    ) {
      return; // Exit if validation fails
    }

    if (!connectionId) {
      try {
        const newConnectionId = await saveNewConnection(
          connectionDetails,
          endpointId
        );
        // Fetch connection summaries and set the selected connection
        const result = await dispatch(fetchConnectionSummaries());
        const summaries = unwrapResult(result);
        const connectionSummary = summaries.find(
          (summary) => summary.ConnectionId === newConnectionId.data
        );
        dispatch(setEditConnectionId(newConnectionId.data));
        dispatch(
          setSelectedConnection({ selectedConnectionInfo: connectionSummary })
        );
        await Promise.all([
          dispatch(fetchConnectionIds()),
          dispatch(fetchEndpoints()),
          dispatch(fetchConnectionAllPairs()),
          dispatch(fetchConnectionProfiles()),
          dispatch(fetchOperations()),
        ]);

        // heap.io
        window.heap.track("Connections Manager - Create New Connection");

        dispatch(
          createAlert({
            status: "Success",
            alertType: "result",
            title: "Connection Created!",
            message: "Connection has been created successfully.",
            buttonLabel: "Close",
            onClose: () => {},
          })
        );
      } catch (error) {
        const errorMsg = error?.response?.data?.message || error?.response?.data 
        || error?.message || "An unexpected error has occurred";
        
        dispatch(
          createAlert({
            status: "Error",
            alertType: "result",
            title: "Error Saving New Converted Connection",
            message: errorMsg,
            buttonLabel: "Close",
            onClose: () => {},
          })
        );
      }
    } else {
      handleEditConnection(
        dispatch,
        connectionId,
        connectionDetails,
        modifiedConnectionPairs,
        catalogSharingSetupDetails,
        connectionPairObject,
        workflowModel,
        endpointId,
        () => {}
      );
    }
  };

  const addNewButtonClicked = async () => {
    try {
      //clear all fields & reset for next added connection
      setConnectionDetails({
        connectionName: "",
        displayName: "",
        connectionType: "",
        shortForm: "",
        isDisabled: false,
      });
      setEndpointId(null);
      setShowErrors(false);
      setClearFormKey((prev) => prev + 1);
      setTabErrors({
        connectionDetails: false,
        endpoints: false,
        schema: false,
        connectionPair: false,
        workflows: false,
      });
      setTabValue(0);
      setConnectionTabValue(0);
      dispatch(setSelectedConnection({selectedConnectionInfo: null}));
      dispatch(setEditConnectionId(null));
      
      // Fetch connection summaries and set the selected connection
      await Promise.all([
        dispatch(fetchConnectionSummaries()),
        dispatch(fetchConnectionIds()),
        dispatch(fetchEndpoints()), //required to prep endpoint screen for next connection
        dispatch(fetchConnectionAllPairs()),
        dispatch(fetchConnectionProfiles()),
        dispatch(fetchOperations()),
      ]);

      // heap.io
      window.heap.track("Connections Manager - Cleared Connection Info");
      
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.response?.data 
      || error?.message || "An unexpected error has occurred";
      
      dispatch(
        createAlert({
          status: "Error",
          alertType: "result",
          title: "Error Clearing Connection Info",
          message: errorMsg,
          buttonLabel: "Close",
          onClose: () => {},
        })
      );
      return;
    }
  };

  useEffect(() => {
    if (connectionId) {
      getEndpointSchema(dispatch, connectionId);
    }
  }, [connectionId, dispatch]);

  const editSchemaTabValue = useSelector(selectEditSchemaTabValue);
  const isMapTabSelected = useSelector(selectMapTabSelected);
  const selectedSchema = useSelector(selectSelectedSchema);

  const handleMapping = () => {
    if (selectedSchema === undefined || selectedSchema === null) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message: "A record was not selected to do mapping!",
          buttonLabel: "Close",
        })
      );
      dispatch(setIsMappingClicked({ isMappingClicked: false }));
    } else {
      dispatch(setIsMappingClicked({ isMappingClicked: true }));
    }
  };

  const isMappingClicked = useSelector(selectIsMappingClicked);

  const { registerScrollContainer } = useManageConnectionFooter();
  const containerScrollRef = React.useCallback(
    (node) => {
      if (node) registerScrollContainer(node);
    },
    [registerScrollContainer]
  );

  return (
    <div>
      {/* Footer button rendered via portal to FooterBarHost */}
      <FooterButton
        variant="primary"
        onClick={addNewButtonClicked}
        style={{display: tabValue !== 0 ? "none":"inline-flex"}}
        disabled={!connectionId}
        >
        Add New
      </FooterButton>

      <FooterButton
        variant="primary"
        onClick={saveButtonClicked}
        hidden={isMapTabSelected || tabValue === 1}
      >
        Save
      </FooterButton>

      <Dialog
        className="connection-settings-container"
        open={open}
        onClose={onClose}
        PaperProps={{
          style: {
            backgroundColor: "transparent",
            boxShadow: "none",
            minWidth: "95vw",
            minHeight: "99vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
        sx={{ zIndex: "1500" }}
      >
        <DialogTitle
          className="workflow-settings-heading"
          style={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "var(--primary-main)",
            color: "var(--default-light)",
            fontSize: "24px",
            borderRadius: "15px 15px 0 0",
          }}
        >
          <div style={{ display: "flex" }}>
            <div style={{ fontWeight: "var(--font-semi-bold)" }}>
              Add Connection
            </div>
          </div>
          <IconButton
            className="closeIconButton"
            style={{ color: "var(--default-light)" }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          className="dialogContent"
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            padding: "10px",
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              boxShadow: "0px 1.5px 0px var(--primary-main)",
              backgroundColor: "var(--disabled)",
              pt: 1,
            }}
          >
            <ShepTabs
              variant="primary"
              value={tabValue}
              onChange={handleTabChange}
            >
              <ShepTab variant="primary" key="0" label={"Connection"} />
              <ShepTab
                variant="primary"
                key="1"
                label={"Schema"}
                disabled={!connectionId}
              />
              <ShepTab
                variant="primary"
                key="2"
                label={"Connection Pair"}
                disabled={!connectionId}
              />
              <ShepTab
                variant="primary"
                key="3"
                label={"Workflows"}
                disabled={!connectionId}
              />
            </ShepTabs>
          </Box>
          <SubtabBarHost />
          <Container
            sx={{
              flex: "1 1 auto",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              paddingTop: "10px",
            }}
            ref={containerScrollRef}
          >
            <Box
              sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: "1 1 auto",
                    minHeight: 0,
              }}
            >
              {tabValue === 0 && (
                <Connection
                  key={clearFormKey}
                  connectionDetails={connectionDetails}
                  setConnectionDetails={setConnectionDetails}
                  showErrors={showErrors}
                  setTabErrors={setTabErrors}
                  connectionId={connectionId}
                  selectedEndpointId={endpointId}
                  setSelectedEndpointId={setEndpointId}
                  tabValue={connectionTabValue}
                  setTabValue={setConnectionTabValue}
                />
              )}
              {tabValue === 1 && <Schema />}
              {tabValue === 2 && (
                <ConnectionPair
                  modifiedConnectionPairs={modifiedConnectionPairs}
                  setModifiedConnectionPairs={setModifiedConnectionPairs}
                  showErrors={showErrors}
                  setHasErrors={(hasErrors) =>
                    setTabErrors((prev) => ({
                      ...prev,
                      connectionPair: hasErrors,
                    }))
                  }
                />
              )}
              {tabValue === 3 && <Workflows connectionId={connectionId} />}
            </Box>
          </Container>
          <FooterBarHost />
        </DialogContent>
      </Dialog>
      <Dialog
        open={confirmationOpen}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxWidth: "1200px",
            width: "800px",
            height: "400px",
            borderRadius: "15px",
          },
        }}
        sx={{ zIndex: "1510" }}
      >
        <DialogContent style={{ textAlign: "center", paddingTop: "60px" }}>
          <DialogContentText id="alert-dialog-description">
            <CheckCircleIcon
              sx={{
                alignSelf: "center",
                color: "#4caf50",
                marginLeft: "auto",
                width: 60,
                height: 60,
              }}
            />
            <h3>We Received Your Request!</h3>
            Our team will contact you soon to validate your request.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <div style={{ textAlign: "center", paddingBottom: "60px" }}>
            <Button className="btn-primary" onClick={handleClose} autoFocus>
              Close
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const AddConnectionPopup = (props) => (
  <ManageConnectionPopupProvider>
    <AddConnectionPopupInner {...props} />
  </ManageConnectionPopupProvider>
);

export default AddConnectionPopup;
